import React, { useState } from 'react';
import { motion } from 'motion/react';
import {
  BookOpen,
  CheckCircle2,
  Clock,
  QrCode,
  Eye,
  Trash2,
  Edit3,
  ChevronDown,
  Award,
  GitBranch,
  XCircle,
  Phone,
  MessageCircle,
  Lock,
  AlertTriangle,
  ShieldCheck,
  UserCheck,
  Building2,
  Printer,
  Layers,
  ShoppingCart,
} from 'lucide-react';
import type { Book, BookStatus, User } from '../../../../shared/types/index.js';
import { BookStatusBadge } from './BookStatusBadge.js';
import { isOwnerOrAdmin, isAuthorSubmission, canEditBookMetadata, canDeleteBook } from './bookListUtils.js';
import { BookExpandableDetail } from '../BookExpandableDetail.js';
import { RejectReasonModal } from './RejectReasonModal.js';
import { PublisherVerificationModal } from './PublisherVerificationModal.js';
import { LazyBookCover } from '../../../../shared/components/LazyBookCover.js';
import { resolveCoverUrl } from '../../../../shared/utils/imageCompressor.js';

interface BookCardProps {
  book: Book;
  index: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onOpenModal: (book: Book) => void;
  onOpenTree: (book: Book) => void;
  onOpenCertificate: (book: Book) => void;
  onOpenPrintLabel?: (book: Book) => void;
  onEdit: (book: Book) => void;
  onDelete: (id: string, title: string) => void;
  onUpdateStatus: (id: string, status: BookStatus, note?: string) => void;
  isStatusPending: boolean;
  user: User | null;
}

export const BookCard: React.FC<BookCardProps> = ({
  book,
  index,
  isExpanded,
  onToggleExpand,
  onOpenModal,
  onOpenTree,
  onOpenCertificate,
  onOpenPrintLabel,
  onEdit,
  onDelete,
  onUpdateStatus,
  isStatusPending,
  user,
}) => {
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectActionType, setRejectActionType] = useState<'publisher_reject' | 'admin_reject'>('publisher_reject');
  const [isPublisherVerifyModalOpen, setIsPublisherVerifyModalOpen] = useState(false);

  const isFromAuthor = isAuthorSubmission(book, user);
  const canEdit = canEditBookMetadata(book, user);

  const handleOpenRejectModal = (type: 'publisher_reject' | 'admin_reject') => {
    setRejectActionType(type);
    setIsRejectModalOpen(true);
  };

  const handleConfirmReject = (reason: string) => {
    const targetStatus: BookStatus = rejectActionType === 'publisher_reject' ? 'draft' : 'ditolak';
    onUpdateStatus(book.id, targetStatus, reason);
    setIsRejectModalOpen(false);
  };

  const handleConfirmPublisherVerification = (verificationNote: string) => {
    onUpdateStatus(book.id, 'diajukan', verificationNote);
    setIsPublisherVerifyModalOpen(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: Math.min(index * 0.05, 0.4), ease: 'easeOut' }}
      className={`bg-white rounded-2xl border flex flex-col justify-between overflow-hidden transition-all duration-300 group ${
        isExpanded
          ? 'border-indigo-300 ring-2 ring-indigo-500/10 shadow-md'
          : 'border-slate-200/90 shadow-2xs hover:shadow-lg hover:shadow-indigo-100/40 hover:-translate-y-1.5 hover:border-indigo-300/80'
      }`}
    >
      <div className="p-4 sm:p-5 md:p-6 lg:p-8 space-y-4 md:space-y-0 md:flex md:gap-8 lg:gap-10">
        {/* Cover Section - Enlarged for Desktop */}
        <div className="flex shrink-0 justify-center md:block">
          <LazyBookCover
            coverUrl={resolveCoverUrl(book.coverUrl)}
            title={book.title}
            className="w-20 h-28 sm:w-24 sm:h-34 md:w-36 md:h-50 lg:w-44 lg:h-60 rounded-2xl shadow-lg border border-slate-200/60 transition-transform duration-500 group-hover:scale-[1.02]"
            targetWidth={500}
            quality={90}
            hoverEffect
            priority={index < 4}
          />
        </div>

        {/* Content Section */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Top card bar: status, category, submission source & expand */}
          <div className="flex items-center justify-between gap-3 mb-4 md:mb-5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-3 py-1 rounded-lg bg-indigo-50 text-indigo-700 text-[10px] md:text-[11px] font-extrabold uppercase tracking-widest border border-indigo-100/50 shadow-2xs">
                {book.category}
              </span>

              {/* Source Badge: Differentiate Penulis vs Penerbit Submission */}
              {user?.role === 'penerbit' && (
                isFromAuthor ? (
                  <span className="px-3 py-1 rounded-lg bg-purple-50 text-purple-700 border border-purple-200/50 text-[10px] font-extrabold uppercase tracking-wider inline-flex items-center gap-1.5 shadow-2xs">
                    <UserCheck className="w-3.5 h-3.5 text-purple-600 shrink-0" />
                    <span>Diajukan Penulis</span>
                  </span>
                ) : (
                  <span className="px-3 py-1 rounded-lg bg-slate-50 text-slate-600 border border-slate-200/50 text-[10px] font-extrabold uppercase tracking-wider inline-flex items-center gap-1.5 shadow-2xs">
                    <Building2 className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                    <span>Mandiri</span>
                  </span>
                )
              )}

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenTree(book);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-white hover:bg-indigo-50 text-slate-600 hover:text-indigo-700 text-[10px] md:text-[11px] font-extrabold uppercase tracking-wider transition border border-slate-200 cursor-pointer shadow-2xs active:scale-95"
                title="Lihat Pohon Silsilah Edisi (Edition Tree)"
              >
                <GitBranch className="w-3.5 h-3.5 text-indigo-600" />
                <span>{book.edition || 'Edisi 1'}</span>
              </button>
            </div>

            <div className="flex items-center gap-2">
              <BookStatusBadge status={book.status} />
            </div>
          </div>

          <div className="flex-1 space-y-4 md:space-y-5">
            <div className="space-y-2">
              <h3 className="font-black text-slate-900 text-lg sm:text-xl md:text-2xl lg:text-3xl leading-tight line-clamp-2 group-hover:text-indigo-600 transition-colors duration-300">
                {book.title}
              </h3>
              {book.subtitle && (
                <p className="text-xs md:text-base text-slate-500 font-semibold line-clamp-1 italic tracking-tight">
                  {book.subtitle}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 text-xs md:text-[15px]">
              <div className="space-y-2">
                <p className="text-slate-500 flex items-center gap-3">
                  <UserCheck className="w-4 h-4 text-slate-400" />
                  <span className="truncate">Penulis: <strong className="text-slate-900 font-bold">{book.authorName}</strong></span>
                </p>
                <p className="text-slate-500 flex items-center gap-3">
                  <Building2 className="w-4 h-4 text-slate-400" />
                  <span className="truncate">Penerbit: <strong className="text-slate-800 font-bold">{book.publisherName}</strong></span>
                </p>
              </div>
              <div className="space-y-2">
                <p className="text-slate-500 flex items-center gap-3">
                  <Clock className="w-4 h-4 text-slate-400" />
                  <span>Tahun: <strong className="text-slate-800 font-bold">{book.publicationYear}</strong></span>
                </p>
                <p className="text-slate-500 flex items-center gap-3">
                  <BookOpen className="w-4 h-4 text-slate-400" />
                  <span>Bahasa: <strong className="text-slate-800 font-bold">{book.language || 'Indonesia'}</strong></span>
                </p>
              </div>
              <div className="space-y-2 hidden lg:block">
                <p className="text-slate-500 flex items-center gap-3">
                  <Layers className="w-4 h-4 text-slate-400" />
                  <span>Tipe: <strong className="text-slate-800 font-bold capitalize">{book.editionType || 'Orisinal'}</strong></span>
                </p>
                <p className="text-slate-500 flex items-center gap-3">
                  <ShieldCheck className="w-4 h-4 text-slate-400" />
                  <span>Naskah: <strong className="text-slate-800 font-bold">{book.pages || 0} Hlm</strong></span>
                </p>
              </div>
            </div>

            {/* UIBN Code Badge - Large for Desktop */}
            <div className="mt-6 p-4 md:p-5 rounded-2xl bg-indigo-50/40 border border-indigo-100/60 flex items-center justify-between shadow-xs flex-wrap gap-4">
              <div className="flex items-center gap-5">
                <div className="w-12 h-12 rounded-2xl bg-white border border-indigo-100 flex items-center justify-center text-indigo-600 shadow-sm">
                  <ShieldCheck className="w-7 h-7" />
                </div>
                <div>
                  <span className="text-[10px] uppercase font-black text-slate-400 tracking-widest block mb-0.5">ID Katalog Resmi UIBN</span>
                  <span className="font-mono font-bold text-lg md:text-xl text-indigo-900 tracking-tighter">{book.uibn}</span>
                </div>
              </div>
              
              <div className="flex items-center gap-3 ml-auto">
                {book.purchaseUrl && (
                  <a
                    href={book.purchaseUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white transition-all duration-300 cursor-pointer flex items-center gap-2 font-black text-xs uppercase tracking-wider shadow-lg shadow-emerald-200 hover:-translate-y-0.5"
                    title="Beli Buku Ini (Toko Online)"
                  >
                    <ShoppingCart className="w-5 h-5" />
                    <span>Beli</span>
                  </a>
                )}
                
                <button
                  type="button"
                  onClick={onToggleExpand}
                  className={`px-6 py-2.5 rounded-xl border transition-all duration-300 cursor-pointer flex items-center gap-2 font-black text-xs uppercase tracking-wider ${
                    isExpanded
                      ? 'bg-indigo-600 text-white border-indigo-600 shadow-lg shadow-indigo-200 -translate-y-0.5'
                      : 'bg-white hover:bg-indigo-50 text-indigo-700 border-indigo-200 hover:border-indigo-300 shadow-2xs'
                  }`}
                  title={isExpanded ? 'Tutup Barcode & QR' : 'Tampilkan Barcode & QR'}
                >
                  <QrCode className="w-5 h-5" />
                  <span>{isExpanded ? 'Sembunyikan' : 'Kelola Barcode'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Admin Approval/Rejection Bar for Pending Submissions */}
        {user?.role === 'admin' && book.status === 'diajukan' && (
          <div className="p-2.5 rounded-xl bg-amber-50/90 border border-amber-200/80 space-y-2 text-xs">
            <div className="flex items-center justify-between text-amber-900 font-bold">
              <span className="flex items-center gap-1.5 text-[11px]">
                <Clock className="w-3.5 h-3.5 text-amber-600 animate-pulse" /> Menunggu Verifikasi Admin
              </span>
            </div>
            <div className="flex items-center gap-1.5 pt-1 border-t border-amber-200/60">
              <button
                type="button"
                onClick={() => onUpdateStatus(book.id, 'disetujui')}
                disabled={isStatusPending}
                className="flex-1 py-1.5 px-2 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-bold rounded-lg transition text-[11px] flex items-center justify-center gap-1 cursor-pointer shadow-2xs"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>Setujui</span>
              </button>
              <button
                type="button"
                onClick={() => handleOpenRejectModal('admin_reject')}
                disabled={isStatusPending}
                className="flex-1 py-1.5 px-2 bg-rose-600 hover:bg-rose-700 active:bg-rose-800 text-white font-bold rounded-lg transition text-[11px] flex items-center justify-center gap-1 cursor-pointer shadow-2xs"
              >
                <XCircle className="w-3.5 h-3.5" />
                <span>Tolak</span>
              </button>
            </div>
          </div>
        )}

        {/* Publisher Endorsement Bar for Pending Author Submissions */}
        {user?.role === 'penerbit' && book.status === 'menunggu_penerbit' && book.publisherId === user.id && (
          <div className="p-2.5 rounded-xl bg-indigo-50 border border-indigo-200 space-y-2 text-xs">
            <div className="flex items-center justify-between text-indigo-900 font-bold">
              <span className="flex items-center gap-1.5 text-[11px]">
                <Clock className="w-3.5 h-3.5 text-indigo-600 animate-pulse" /> Konfirmasi Penerbitan (Naskah Penulis)
              </span>
            </div>

            {book.authorWhatsapp && (
              <div className="p-2 rounded-lg bg-white border border-indigo-100 flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  <Phone className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  <span className="text-[11px] text-slate-700 truncate font-medium">
                    WA Penulis: <strong className="font-mono text-slate-900">{book.authorWhatsapp}</strong>
                  </span>
                </div>
                <a
                  href={`https://wa.me/${book.authorWhatsapp.replace(/[^0-9]/g, '').replace(/^0/, '62')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-2 py-0.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-[10px] font-bold flex items-center gap-1 shrink-0"
                >
                  <MessageCircle className="w-3 h-3" />
                  <span>Chat WA</span>
                </a>
              </div>
            )}

            <div className="flex items-center gap-1.5 pt-1 border-t border-indigo-200/60">
              <button
                type="button"
                onClick={() => setIsPublisherVerifyModalOpen(true)}
                disabled={isStatusPending}
                className="flex-1 py-1.5 px-2 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white font-bold rounded-lg transition text-[11px] flex items-center justify-center gap-1 cursor-pointer shadow-2xs"
                title="Verifikasi syarat kelayakan naskah sebelum dikirim ke Admin UIBN"
              >
                <ShieldCheck className="w-3.5 h-3.5" />
                <span>Verifikasi</span>
              </button>
              <button
                type="button"
                onClick={() => handleOpenRejectModal('publisher_reject')}
                disabled={isStatusPending}
                className="flex-1 py-1.5 px-2 bg-slate-500 hover:bg-slate-600 active:bg-slate-700 text-white font-bold rounded-lg transition text-[11px] flex items-center justify-center gap-1 cursor-pointer shadow-2xs"
                title="Tolak naskah atau minta revisi ke penulis"
              >
                <XCircle className="w-3.5 h-3.5" />
                <span>Tolak/Revisi</span>
              </button>
            </div>
          </div>
        )}

        {/* Feedback / Rejection Note banner */}
        {(book.status === 'draft' || book.status === 'ditolak' || Boolean(book.editionNotes)) && (
          <div
            className={`p-3 rounded-xl border text-xs space-y-2 shadow-2xs ${
              book.status === 'ditolak'
                ? 'bg-rose-50/95 border-rose-200 text-rose-950'
                : 'bg-amber-50/95 border-amber-300 text-amber-950 ring-1 ring-amber-400/20'
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <span
                className={`font-bold text-[11px] flex items-center gap-1.5 ${
                  book.status === 'ditolak' ? 'text-rose-800' : 'text-amber-900'
                }`}
              >
                {book.status === 'ditolak' ? (
                  <XCircle className="w-4 h-4 text-rose-600 shrink-0" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                )}
                <span>
                  {user?.role === 'penerbit'
                    ? book.status === 'ditolak'
                      ? 'Naskah Telah Ditolak Redaksi'
                      : 'Catatan Penolakan / Revisi yang Anda Berikan:'
                    : book.status === 'ditolak'
                    ? 'Pengajuan UIBN Ditolak'
                    : book.editionNotes
                    ? 'Catatan Revisi / Masukan Penerbit:'
                    : 'Naskah Berstatus Draf (Perlu Tindakan):'}
                </span>
              </span>
              <span
                className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider shrink-0 ${
                  book.status === 'ditolak'
                    ? 'bg-rose-200 text-rose-800'
                    : 'bg-amber-200 text-amber-900'
                }`}
              >
                {book.status === 'ditolak' ? 'Ditolak' : 'Perlu Revisi'}
              </span>
            </div>

            <p className="text-slate-800 bg-white p-2.5 rounded-lg border border-amber-200/80 font-mono text-[11px] break-words leading-relaxed shadow-2xs">
              {book.editionNotes ||
                (user?.role === 'penerbit'
                  ? 'Naskah ini telah ditolak oleh redaksi Anda. Menunggu Penulis melakukan perbaikan metadata atau mengajukan ulang.'
                  : book.status === 'ditolak'
                  ? 'Pengajuan naskah ini ditolak oleh verifikator. Silakan tinjau kembali metadata atau perbaiki naskah sebelum mengajukan ulang.'
                  : book.publisherId
                  ? 'Naskah berada dalam status draf untuk kurasi penerbit terdaftar. Silakan lengkapi atau revisi metadata naskah lalu klik tombol Kirim Ulang ke Penerbit.'
                  : 'Naskah tersimpan sebagai draf. Hak pengeditan metadata naskah dibuka penuh.')}
            </p>

            {/* Render Edit & Resubmit buttons ONLY FOR AUTHOR or for publisher's OWN self-registered book */}
            {user?.role === 'penulis' && (
              <div className="pt-2 border-t border-amber-200/70 flex flex-wrap items-center justify-between gap-2">
                <span className="text-[10px] text-slate-600 font-medium">
                  {book.status === 'ditolak'
                    ? 'Perbaiki naskah lalu ajukan ulang verifikasi:'
                    : 'Metadata naskah terbuka untuk diperbaiki:'}
                </span>
                <div className="flex items-center gap-1.5">
                  {canDeleteBook(book, user) && (
                    <button
                      type="button"
                      onClick={() => onDelete(book.id, book.title)}
                      className="px-2 py-1 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 rounded-md font-semibold text-[11px] flex items-center gap-1 cursor-pointer transition shadow-2xs"
                      title="Hapus Draft UIBN ini"
                    >
                      <Trash2 className="w-3 h-3 text-rose-600" />
                      <span>Hapus Draft</span>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => onEdit(book)}
                    className="px-2.5 py-1 bg-white hover:bg-slate-50 text-indigo-700 border border-indigo-200 rounded-md font-semibold text-[11px] flex items-center gap-1 cursor-pointer transition shadow-2xs"
                  >
                    <Edit3 className="w-3 h-3 text-indigo-600" />
                    <span>Perbaiki Naskah</span>
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      onUpdateStatus(
                        book.id,
                        book.publisherId ? 'menunggu_penerbit' : 'diajukan'
                      )
                    }
                    disabled={isStatusPending}
                    className="px-2.5 py-1 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white rounded-md font-bold text-[11px] flex items-center gap-1 cursor-pointer transition shadow-2xs"
                  >
                    <span>
                      {book.publisherId
                        ? 'Kirim Ulang ke Penerbit'
                        : 'Ajukan Ulang ke Admin'}
                    </span>
                  </button>
                </div>
              </div>
            )}

            {/* Notice for Penerbit on rejected author submission */}
            {user?.role === 'penerbit' && isFromAuthor && (
              <div className="pt-1.5 border-t border-amber-200/70 flex items-center justify-between text-[11px] text-slate-600">
                <span className="font-semibold text-rose-800">
                  Status: Menunggu Penulis melakukan revisi metadata.
                </span>
                {book.authorWhatsapp && (
                  <a
                    href={`https://wa.me/${book.authorWhatsapp.replace(/[^0-9]/g, '').replace(/^0/, '62')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-700 font-bold hover:underline flex items-center gap-1"
                  >
                    <MessageCircle className="w-3.5 h-3.5 text-emerald-600" />
                    <span>Hubungi Penulis</span>
                  </a>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom Card Footer */}
      <div className="px-3.5 sm:px-4 py-2.5 bg-slate-50/70 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2 text-xs">
        {/* Expand Detail Toggle Button */}
        <button
          type="button"
          id={`btn-expand-${book.id}`}
          onClick={onToggleExpand}
          className={`px-2.5 py-1.5 text-xs font-semibold rounded-lg transition flex items-center gap-1.5 cursor-pointer min-h-[44px] ${
            isExpanded
              ? 'bg-indigo-600 text-white shadow-2xs'
              : 'text-indigo-700 bg-indigo-50 hover:bg-indigo-100 active:bg-indigo-200'
          }`}
          title={isExpanded ? 'Tutup Expandable Detail' : 'Buka Expandable Detail View'}
        >
          <span>{isExpanded ? 'Tutup Detail' : 'Detail & Barcode'}</span>
          <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
        </button>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onOpenModal(book)}
            className="px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:text-indigo-600 hover:bg-white rounded-lg transition flex items-center justify-center gap-1 cursor-pointer min-h-[44px]"
          >
            <Eye className="w-3.5 h-3.5" />
            <span>Modal</span>
          </button>

          <button
            type="button"
            onClick={() => onOpenTree(book)}
            className="p-1.5 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 active:bg-indigo-100 rounded-md transition cursor-pointer min-h-[44px] min-w-[44px] flex items-center justify-center"
            title="Silsilah Edisi (Book Edition Tree)"
          >
            <GitBranch className="w-3.5 h-3.5" />
          </button>

          {isOwnerOrAdmin(book, user) && book.status === 'disetujui' && (
            <>
              <button
                type="button"
                onClick={() => onOpenCertificate(book)}
                className="p-1.5 text-amber-600 hover:text-amber-700 hover:bg-amber-50 active:bg-amber-100 rounded-md transition cursor-pointer min-h-[44px] min-w-[44px] flex items-center justify-center"
                title="E-Sertifikat Digital (PDF)"
              >
                <Award className="w-3.5 h-3.5" />
              </button>

              {onOpenPrintLabel && (
                <button
                  type="button"
                  onClick={() => onOpenPrintLabel(book)}
                  className="p-1.5 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 active:bg-emerald-100 rounded-md transition cursor-pointer min-h-[44px] min-w-[44px] flex items-center justify-center"
                  title="Cetak Label & Barcode UIBN (Printer-Friendly)"
                >
                  <Printer className="w-3.5 h-3.5" />
                </button>
              )}
            </>
          )}

          {/* Edit Button: strictly respects canEditBookMetadata */}
          {isOwnerOrAdmin(book, user) && (
            canEdit ? (
              <button
                type="button"
                onClick={() => onEdit(book)}
                className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-white active:bg-slate-200 rounded-md transition cursor-pointer min-h-[44px] min-w-[44px] flex items-center justify-center"
                title="Ubah Metadata Buku"
              >
                <Edit3 className="w-3.5 h-3.5" />
              </button>
            ) : (
              <div
                className="p-1.5 text-slate-300 cursor-not-allowed select-none min-h-[44px] min-w-[44px] flex items-center justify-center"
                title={
                  user?.role === 'penerbit' && isFromAuthor
                    ? 'Metadata dikelola oleh Penulis. Penerbit tidak dapat mengedit metadata langsung.'
                    : 'Metadata terkunci selama peninjauan/setelah disetujui UIBN'
                }
              >
                <Lock className="w-3.5 h-3.5" />
              </div>
            )
          )}

          {canDeleteBook(book, user) && (
            <button
              type="button"
              onClick={() => onDelete(book.id, book.title)}
              className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-white active:bg-rose-50 rounded-md transition cursor-pointer min-h-[44px] min-w-[44px] flex items-center justify-center"
              title="Hapus Draft UIBN"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Inline Expandable Detail View */}
      {isExpanded && (
        <div className="px-4 pb-4 sm:px-5 sm:pb-5">
          <BookExpandableDetail
            book={book}
            onOpenModal={onOpenModal}
            onEdit={onEdit}
            onCollapse={onToggleExpand}
          />
        </div>
      )}

      {/* Reject / Revision Note Modal */}
      <RejectReasonModal
        isOpen={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        onConfirm={handleConfirmReject}
        bookTitle={book.title}
        actionType={rejectActionType}
        isPending={isStatusPending}
      />

      {/* Publisher Verification Checklist Modal */}
      <PublisherVerificationModal
        isOpen={isPublisherVerifyModalOpen}
        onClose={() => setIsPublisherVerifyModalOpen(false)}
        onConfirm={handleConfirmPublisherVerification}
        book={book}
        isPending={isStatusPending}
      />
    </motion.div>
  );
};
